import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { TSession } from '../Sessions';
import { TLabelTask, Cursor, PositionBuffer, TaskStatus } from './SessionLabelTask';
import SessionTimeline, { MarkerGraphics } from '../Timeline/SessionTimeline';
import VideoControls from '../../VideoPlayer/VideoControls';
import VideoLayout, { TextOverlay, PanMethod } from '../../VideoPlayer/VideoLayout';
import LabelBallSidebar from './LabelBallSidebar';
import LabelCircularProgress from './LabelCircularProgress';
import GoBackButton from './Buttons/GoBackButton';
import { makeStyles } from '@material-ui/core';
import LabelDebugInfoButton from './Buttons/LabelDebugInfoButton';
import { ClickInfo, MouseButton } from './useClickHandler';
import LabelContextMenu, { ContextItem } from './LabelContextMenu';
import { BallActionType, BallAction, getActionColor, getActionIcon, getActionLabelByType } from './BallActionList';
import { Coordinate, Video, VideoOverlayObject } from '../../VideoPlayer/VideoTypes';
import { fetchPost, fetchDelete, fetchGet } from '../../Util/fetchAPI';
import LabelSubMenu from './LabelSubMenu';
import { ReactComponent as KeeperSvg } from '../../../graphics/keeper.svg';
import useInterval from '../../Util/useInterval';
import useMouseRipple from './useMouseRipple';
import useSound from 'use-sound';
import { useHotkeys } from 'react-hotkeys-hook';
import AreYouSure from '../../Util/AreYouSure';
import { projection } from '../../Util/calib';
import BallMarker from '../../VideoOverlayObjects/BallMarker';
import BallActionMarker from '../../VideoOverlayObjects/BallActionMarker';
import RecalculateButton from './Buttons/RecalculateButton';
import Trail from '../../VideoOverlayObjects/Trail';
import ToggleMenuItem from '../../VideoPlayer/Menu/ToggleMenuItem';
import useSupervisorFunctions from './useSupervisorFunctions';
import { saneRound } from '../../Util/functions';
import useLocalStorage from '../../Util/useLocalStorage';
import LegacyLabelAssistantToggleButton from './Buttons/LegacyLabelAssistantToggleButton';

const useStyles = makeStyles(() => ({
  flexContainer: {
    display: 'flex',
  },
  sidebar: {
    width: '320px',
    minWidth: '320px',
    height: '60vh',
    overflowY: 'scroll',
  },
  sidebarRow: {
    padding: '8px 0px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  videoContainer: {
    width: '80%',
    height: '60vh',
  },
  videoControlsContainer: {
    marginTop: '12px',
    width: '320px',
    minWidth: '320px',
    height: '12vh',
    minHeight: '100px',
    backgroundColor: '#ccf1',
    border: '1px dashed #000',
  },
  timelineContainer: {
    marginTop: '12px',
    width: '80%',
    height: '12vh',
  },
  buttonsTitle: {
    marginTop: 12,
    fontSize: '12px',
    color: '#444',
  },
  sidebarSpacer: {
    marginTop: '12px',
    width: '100%',
  },
}));

type ClickAction = {
  viewId: string;
  info: ClickInfo;
};

type HeightIndication = {
  frameId: number;
  wasInAir: boolean;
  position: Coordinate;
  ground_video_coordinates: Map<string, Coordinate>;
}

enum GameState {
  ALIVE,
  DEAD,
}

type GameStatus = {
  first_frame: number;
  last_frame: number;
  start_state: GameState;
  transitions: number[];
};

type Props = {
  session: TSession;
  task: TLabelTask;
  runId: string;
  reloadTask: () => void;
};

// Update videoplayer every ms
const updateIntervalMs: number = 40;
// Fetch actions pre & post
const fetchActionsIntervalMs: number = 3000;
const fetchActionsPre: number = 25;
const fetchActionsPost: number = 25;
// Fetch positions size
const fetchPositionBufferSize: number = 250;
const fetchPositionFutureBuffers: number = 6;
// Graphics
const showActionsSeconds: number = 2;
// Total length of the proximity trail in seconds
const proximityTrailSeconds: number = 4;
// Ball Z value when on the ground
export const ballGroundZ: number = 0.11;

const BallLabelTask = (props: Props) => {
  const classes = useStyles();

  const { session, task, runId, reloadTask } = props;
  const taskId: string = String(task.id);

  const fps: number =
    (session && session.videos && session?.videos.length > 0 && session?.videos[0].fps) || 25;
  const startFrame: number = task?.startFrame || 0;
  const startTime: number = startFrame / fps;
  const endFrame: number = task?.endFrame || 0;
  const endTime: number = endFrame / fps;

  const { rippleComponents, addRipple } = useMouseRipple({
    border: '1px dashed #ff0',
    backgroundColor: '#ff02',
  });

  const [playBoopSound] = useSound('/sounds/boop.ogg');

  const [cursor] = useState<Cursor>(Cursor.Default);
  const [videoPlaying, setVideoPlaying] = useState<boolean>(false);
  const [playTime, setPlayTime] = useState<number>(startTime);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(0.5);
  const [seekVideo, setSeekVideo] = useState<boolean>(false);
  const [timeStep, setTimeStep] = useState<number>(1);
  const [clickDelay, setClickDelay] = useState<number>(0);
  const [ballActions, setBallActions] = useState<BallAction[]>([]);
  const [positionBuffers, setPositionBuffers] = useState<PositionBuffer[]>([]);
  const [isFetchingPositions, setIsFetchingPositions] = useState<boolean>(false);
  const [lastBufferFrameId, setLastBufferFrameId] = useState<number>(startFrame);
  const [lastRightClick, setLastRightClick] = useState<ClickAction | undefined>(undefined);
  const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
  const [showSubMenu, setShowSubMenu] = useState<boolean>(false);
  const [newActionType, setNewActionType] = useState<BallActionType>(BallActionType.IDLE);
  const [lastLabeledFrameId, setLastLabeledFrameId] = useState<number>(0);
  const [taskStatus, setTaskStatus] = useState<TaskStatus | undefined>(undefined);
  const [gameStatus, setGameStatus] = useState<GameStatus | undefined>(undefined);
  const [nextAction, setNextAction] = useState<BallAction | undefined>(undefined);
  const [actionHappenedFrameIds, setActionHappenedFrameIds] = useState<number[]>([]);
  const [endTaskDialog, setEndTaskDialog] = useState<boolean>(false);
  const [heightIndication, setHeightIndication] = useState<HeightIndication | undefined>(undefined);
  const [trySaveAction, setTrySaveAction] = useState<BallActionType | undefined>(undefined);
  const [showTrail, setShowTrail] = useState<boolean>(true);
  const [legacyBallActionsEnabled, setLegacyBallActionsEnabled] = useLocalStorage<boolean>('legacyBallActionsEnabled', false);
  const [baseLabelUrl, setBaseLabelUrl] = useState<string>('/gateway/label-assistant-v2/api/v1/label');

  const { 
    sidebarButtons: supervisorFunctions, 
    llfAsEndChoice, 
    deleteChoice,
  } = useSupervisorFunctions({
    session,
    task,
    lastLabeledFrameId,
    reloadTask,
  });

  /* Sound mute with m (default off) */
  const [muted, setMuted] = useState<boolean>(true);
  const toggleMute = useCallback(() => {
    setMuted((prev) => !prev);
  }, []);
  useHotkeys('m', toggleMute, { keydown: false, keyup: true }, [toggleMute]);
  /* End sound */

  useEffect(() => {
    setBaseLabelUrl(legacyBallActionsEnabled ? '/gateway/label-assistant' : '/gateway/label-assistant-v2/api/v1/label');
  }, [legacyBallActionsEnabled]);

  const handleLegacyBallActionsToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLegacyBallActionsEnabled(event.target.checked);
    },
    [setLegacyBallActionsEnabled]
  );

  const currentFrameId: number = saneRound(playTime * fps);

  let latestAction: BallAction | undefined =
    ballActions.length > 0
      ? ballActions.reduce((max, action) => {
          return action?.frame_id <= currentFrameId && action?.frame_id > max?.frame_id ? action : max;
        })
      : undefined;
  if (latestAction && latestAction?.frame_id > currentFrameId) {
    latestAction = undefined;
  }

  const setVideoPlayingState = useCallback(
    (newPlayingState: boolean) => {
      if (showSubMenu || showContextMenu) {
        return;
      }

      setVideoPlaying((prevState) => {
        if (prevState === newPlayingState) return prevState;
        return newPlayingState;
      });
    },
    [showContextMenu, showSubMenu]
  );

  const fetchTaskStatus = useCallback(() => {
    fetchGet(`${baseLabelUrl}/run/${runId}/ball/${taskId}/status`).then((status: TaskStatus) => {
      console.log('[TASK_STATUS]', status);
      setTaskStatus(status);
    });
  }, [baseLabelUrl, runId, taskId]);

  const fetchGameStatus = useCallback(() => {
    // fetching game status to know if ball is alive at the start of the task
    fetchGet(`${baseLabelUrl}/run/${runId}/ball/${taskId}/aliveness`).then(
      (status: GameStatus) => {
        console.log('[GAME_STATUS]', status);
        setGameStatus(status);
      }
    );
  }, [baseLabelUrl, runId, taskId]);

  const fetchActions = useCallback(
    (frameId: number) => {
      // Refetch actions on seek
      fetchGet(
        `${baseLabelUrl}/run/${runId}/ball/${taskId}/actions?page_center=${frameId}&pre=${fetchActionsPre}&post=${fetchActionsPost}`
      ).then((data) => {
        console.log('[ACTIONS]', data);
        const filteredActions: BallAction[] = data.value ? data.value.filter((v: BallAction | null) => v !== null) : [];
        setBallActions(filteredActions);

        // Set LLF to max frame id coming from ballactions if above our current LLF
        let maxFrameId: number = 0;
        filteredActions.forEach((a: BallAction) => {
          maxFrameId = a.frame_id > maxFrameId ? a.frame_id : maxFrameId;
        });
        setLastLabeledFrameId((prev: number) => (prev > maxFrameId ? prev : maxFrameId));
      });
    },
    [baseLabelUrl, runId, taskId]
  );

  const fetchPositions = useCallback(
    (frameId: number) => {
      setIsFetchingPositions(true);
      // Refetch positions on seek
      fetchGet(`${baseLabelUrl}/run/${runId}/ball/${taskId}/positions?page_start=${frameId}&page_size=${fetchPositionBufferSize}`).then((data) => {
        // First do a correction on page_start, we want this to be the frame of our very first data frame (instead of fetched position)
        if (data.value.length > 0) {
          data.page_start = data.value[0].frame_id;
        }
        console.log('[POSITIONS]', data);

        setPositionBuffers((prev: PositionBuffer[]) => [...prev, data]);
        setLastBufferFrameId(data.page_start + data.page_size - 1);
        setIsFetchingPositions(false);
      });
    },
    [baseLabelUrl, runId, taskId]
  );

  const onSeek = useCallback((time: number) => {
    setSeekVideo(true);
    // Reset
    setNextAction(undefined);
    // Fetch new actions & positions
    const frameId: number = saneRound(time * fps);
    fetchActions(frameId);
    fetchPositions(frameId);
  }, [fetchActions, fetchPositions, fps]);

  const absoluteSeek = useCallback(
    (newTime: number) => {
      setPlayTime(prev => {
        // Clamp time to allowed values
        const clampedTime = Math.max(startTime, Math.min(newTime, endTime));
        onSeek(clampedTime);
        return clampedTime;
      });
    },
    [onSeek, startTime, endTime]
  );

  const relativeSeek = useCallback((timeDiff: number) => {
    setPlayTime(prev => {
      // Clamp time to allowed values
      const clampedTime = Math.max(startTime, Math.min(prev+timeDiff, endTime));
      onSeek(clampedTime);
      return clampedTime;
    });
  }, [endTime, onSeek, startTime])

  const displaySubMenu = useCallback((actionType: BallActionType) => {
    setShowSubMenu(true);
    setNewActionType(actionType);
  }, []);

  const onCloseSubMenu = useCallback((isSaved: boolean) => {
    setLastRightClick(undefined);
    if(!isSaved) {
      setHeightIndication(undefined);
    }
    setShowSubMenu(false);
  }, []);

  const onCloseContextMenu = useCallback(() => {
    setLastRightClick(undefined);
    setShowContextMenu(false);
  }, []);

  const seekToAction = useCallback(
    (action: BallAction) => {
      absoluteSeek(action.frame_id / fps);
    },
    [fps, absoluteSeek]
  );

  const resetPositions = useCallback(() => {
    setLastBufferFrameId(0);
    setPositionBuffers([]);
  }, []);

  const annotation = useCallback(
    (action: BallAction) => {
      fetchPost(`${baseLabelUrl}/run/${runId}/ball/${taskId}/actions`, action).then((data) => {
        console.log('[ADD ACTION]', data);
        // Fetch new action list
        fetchActions(action.frame_id);
        // Clear positions to refetch them
        resetPositions();
        // Get new game status
        fetchGameStatus();

        // Update last labeled frame id if we surpassed it
        if (action.frame_id > lastLabeledFrameId) {
          setLastLabeledFrameId(action.frame_id);
        }
      });
    },
    [baseLabelUrl, fetchActions, fetchGameStatus, lastLabeledFrameId, resetPositions, runId, taskId]
  );

  const endTask = useCallback(() => {
    // Send an END type annotation
    const position: Coordinate = { x: 0, y: 0, z: ballGroundZ };
    const newAction: BallAction = {
      position: position,
      frame_id: endFrame,
      type: BallActionType.END,
      was_in_air: false,
    };
    annotation(newAction);
    // Close dialog
    setEndTaskDialog(false);
  }, [annotation, endFrame]);

  const deleteAction = useCallback(
    (action: BallAction) => {
      fetchDelete(`${baseLabelUrl}/run/${runId}/ball/${taskId}/actions/${action.frame_id}`).then(
        (success: boolean) => {
          if (success) {
            console.log('[DELETE ACTION]', success);
            // Fetch new action list
            fetchActions(action.frame_id);
            // Clear positions to refetch them
            resetPositions();
            // Get new game status
            fetchGameStatus();

            // If this action was our last labeled action, we want to adjust our last labeled frame id (so get new task status)
            if (action.frame_id === lastLabeledFrameId) {
              fetchTaskStatus();
            }
          } else {
            console.log('[ERROR DELETE ACTION]');
          }
        }
      );
    },
    [baseLabelUrl, fetchActions, fetchGameStatus, fetchTaskStatus, lastLabeledFrameId, resetPositions, runId, taskId]
  );

  const saveClickAnnotation = useCallback((
      click: ClickAction,
      typeOverride: BallActionType | undefined = undefined, 
      wasInAirOverride: boolean | undefined = undefined, 
      zValOverride: number | undefined = undefined
      ) => {

      const video: Video | undefined | null = session?.videos?.find(
        (vid: Video) => String(vid.id) === click.viewId
      );
      if (!video) return;

      // TODO; refactor function
      const wasInAir: boolean = latestAction !== undefined && (latestAction.position.z || ballGroundZ) > ballGroundZ;
      let fastType: BallActionType = typeOverride || BallActionType.TOUCH;
      let useZvalue: number = zValOverride || ballGroundZ;
      if (latestAction && !typeOverride) {
        switch (latestAction.type) {
          case BallActionType.BALL_OUT:
            fastType = BallActionType.BALL_IN;
            break;
          case BallActionType.GAME_PAUSE:
            fastType = BallActionType.GAME_PLAY;
            break;
          case BallActionType.KEEPER_HOLD:
          case BallActionType.KEEPER_TOUCH:
            fastType = BallActionType.KEEPER_TOUCH;
            useZvalue = zValOverride ? zValOverride : latestAction.position.z !== undefined ? Number(latestAction.position.z) : ballGroundZ;
            break;
        }
      }

      const coord: Coordinate = { x: click.info.x, y: click.info.y };
      const subtractForClickTimeSeconds: number =
      click.info.clickTime && playbackSpeed && videoPlaying
          ? click.info.clickTime * playbackSpeed * 0.001 + clickDelay
          : 0;
      const frameId: number = saneRound((playTime - subtractForClickTimeSeconds) * fps);

      const calib = JSON.parse(video?.calibration || '');

      const cameraCoord: number[] = [coord.x, coord.y];
      //get the 3d position from clicked point
      fetchPost('/gateway/calibrations/reprojection', {
        calibration: calib,
        camera_coordinates: [cameraCoord],
        z_const: useZvalue,
      }).then((data: number[][]) => {
        const position: Coordinate = { x: data[0][0], y: data[0][1], z: useZvalue };
        const newAction: BallAction = {
          position: position,
          frame_id: frameId,
          type: typeOverride ? typeOverride : fastType,
          was_in_air: wasInAirOverride ? wasInAirOverride : wasInAir,
        };
        annotation(newAction);
        setLastRightClick(undefined);
      });
    },
    [annotation, clickDelay, fps, latestAction, playTime, playbackSpeed, session, videoPlaying]
  );

  const saveHeightIndicationAnnotation = useCallback(
    (type: BallActionType) => {
      if (!heightIndication) {
        console.log("Height indication is undefined, so not saving");
        return;
      }
      
      const newAction: BallAction = {
        position: {
          x: heightIndication.position.x, 
          y: heightIndication.position.y, 
          z: (heightIndication.position.z || ballGroundZ)
        },
        frame_id: heightIndication.frameId,
        type: type,
        was_in_air: heightIndication.wasInAir,
      };
      annotation(newAction);
    },
    [heightIndication, annotation]
  );

  const handleContextTypeSelection = useCallback(
    (actionType: BallActionType) => {
      // Hide context menu
      setShowContextMenu(false);
      if (lastRightClick 
        && (
            actionType === BallActionType.BOUNCE 
            || actionType === BallActionType.GAME_PLAY
            || actionType === BallActionType.ROLL
          )
        ) {
        // No submenu required on these types
        const wasInAir: boolean = actionType === BallActionType.BOUNCE;
        saveClickAnnotation(lastRightClick, actionType, wasInAir, ballGroundZ);
        setLastRightClick(undefined);
        setVideoPlayingState(true);
      } else {
        // If not handled before, we display the submenu for this type
        displaySubMenu(actionType);
      }
    },
    [displaySubMenu, lastRightClick, saveClickAnnotation, setVideoPlayingState]
  );

  const handleVideoViewClick = useCallback(
    (viewId: string, info: ClickInfo) => {
      // Don't handle right clicks on context menu
      if (showContextMenu || showSubMenu || info.button !== MouseButton.RightMouseButton) {
        return;
      }

      // Show a ripple when a valid click was performed
      addRipple(info.pageX, info.pageY);

      const newClick: ClickAction = {
        viewId: viewId,
        info: info,
      };
      // And set our last right click
      setLastRightClick(newClick);

      if (showContextMenu || showSubMenu) return;

      if (info.longPress) {
        // Long press action
        const subtractForClickTimeSeconds: number =
          info.clickTime && playbackSpeed && videoPlaying
            ? info.clickTime * playbackSpeed * 0.001 + clickDelay
            : 0;
        setVideoPlayingState(false);
        setPlayTime((curTime) => curTime - subtractForClickTimeSeconds);
        setShowContextMenu(true);
      } else {
        // Fast annotation
        saveClickAnnotation(newClick);
      }
    },
    [addRipple, clickDelay, playbackSpeed, saveClickAnnotation, setVideoPlayingState, showContextMenu, showSubMenu, videoPlaying]
  );

  const recalculateRightClickCoords = useCallback((wasInAir: boolean, zEstimate: number) => {
    if(!lastRightClick) return;

    const subtractForClickTimeSeconds: number =
    lastRightClick.info.clickTime && playbackSpeed && videoPlaying
      ? lastRightClick.info.clickTime * playbackSpeed * 0.001 + clickDelay
      : 0;
    const frameId: number = saneRound((playTime - subtractForClickTimeSeconds) * fps);

    // First we need to have the 3d position from our right click
    const coord: Coordinate = { x: lastRightClick.info.x, y: lastRightClick.info.y };

    const video: Video | undefined | null = session?.videos?.find(
      (vid: Video) => String(vid.id) === lastRightClick.viewId
    );
    if (!video) return;
    const calib = JSON.parse(video?.calibration || '');

    const cameraCoord: number[] = [coord.x, coord.y];
    fetchPost('/gateway/calibrations/reprojection', {
      calibration: calib,
      camera_coordinates: [cameraCoord],
      z_const: zEstimate,
    }).then((data: number[][] | undefined) => {
      if(!data) return;

      const position: Coordinate = { x: data[0][0], y: data[0][1], z: zEstimate, };
      // Now get all our ground video coordinates
      const groundCoords: Map<string, Coordinate> = new Map<string, Coordinate>();
      const worldPositions: number[][] = [[position.x, position.y, 0]];

      // Project Positions
      const projectedData = projection(calib, worldPositions);

      groundCoords.set(String(video.id), {
        x: projectedData[0][0],
        y: projectedData[0][1],
      });
      
      // Update last right click position and ground video coords
      setHeightIndication({
        frameId: frameId,
        wasInAir: wasInAir,
        position: position,
        ground_video_coordinates: groundCoords,
      });
    });
  }, [clickDelay, fps, lastRightClick, playTime, playbackSpeed, session, videoPlaying]);

  const onDoneSubMenu = useCallback(
    (type: BallActionType, zEstimate: number, wasInAir: boolean) => {
      setTrySaveAction(type);
    },
    []
  );

  const onChangeSubMenu = useCallback(
    (wasInAir: boolean, zEstimate: number) => {
      if(showSubMenu) {
        recalculateRightClickCoords(wasInAir, zEstimate);
      }
    },
    [showSubMenu, recalculateRightClickCoords]
  )

  const getContextMenuItem = useCallback(
    (type: BallActionType) => {
      return {
        label: getActionLabelByType(type),
        icon: getActionIcon(type),
        style: {
          borderColor: getActionColor(type),
        },
        onClick: () => handleContextTypeSelection(type),
      };
    },
    [handleContextTypeSelection]
  );

  const getMarkerObject = useCallback(
    (type: BallActionType) => {
      return {
        timestamps: ballActions.filter((b) => b.type === type).map((ba) => ba.frame_id / fps),
        color: getActionColor(type),
      };
    },
    [ballActions, fps]
  );

  const projectPositions = useCallback(
    (bufferIndex: number) => {
      if (positionBuffers.length <= bufferIndex) return;

      const positionBuffer: PositionBuffer = positionBuffers[bufferIndex];
      if (positionBuffer.page_size === 0) return;

      positionBuffer.video_coordinates = new Map<string, Coordinate[]>();
      positionBuffer.ground_video_coordinates = new Map<string, Map<number, Coordinate>>();

      // Go through all videos
      session?.videos?.forEach((video: Video) => {
        // Get our calibration
        const calib = JSON.parse(video?.calibration || '');

        // Get our world-, and if necessary also our ground positions
        const worldPositions: number[][] = [];
        const groundPositions: number[][] = [];
        const groundFrameIds: number[] = [];

        let curFrameId: number = positionBuffer.page_start;
        positionBuffer?.value?.forEach((value: Coordinate) => {
          const pos: number[] = [value.x || 0, value.y || 0, value.z || 0];
          worldPositions.push(pos);

          // Check if we need to project the ground position as well
          if (pos[2] > 0.1) {
            groundPositions.push([pos[0], pos[1], 0]);
            groundFrameIds.push(curFrameId);
          }
          curFrameId++;
        });

        // Project Positions
        let projectedData = projection(calib, worldPositions);
        const projCoords: Coordinate[] = [];
        for (let i: number = 0; i < projectedData.length; ++i) {
          const coord = projectedData[i];
          projCoords.push({
            x: coord[0],
            y: coord[1],
          });
        }

        setPositionBuffers((prev: PositionBuffer[]) => {
          if (!prev || prev.length <= bufferIndex) return [];
          const updateBuffer: PositionBuffer = prev[bufferIndex];
          if (!updateBuffer.video_coordinates) return [];
          updateBuffer.video_coordinates.set(String(video.id), projCoords);
          return [...prev];
        });

        // Project Ground Positions
        if (groundPositions.length > 0) {
          projectedData = projection(calib, groundPositions);
          const projCoords: Map<number, Coordinate> = new Map<number, Coordinate>();
          for (let i: number = 0; i < projectedData.length; ++i) {
            let coord = projectedData[i];
            projCoords.set(groundFrameIds[i], {
              x: coord[0],
              y: coord[1],
            });
          }

          setPositionBuffers((prev: PositionBuffer[]) => {
            if (!prev || prev.length <= bufferIndex) return [];
            const updateBuffer: PositionBuffer = prev[bufferIndex];
            if (!updateBuffer.ground_video_coordinates) return [];
            updateBuffer.ground_video_coordinates.set(String(video.id), projCoords);
            return [...prev];
          });
        }
      });
    },
    [positionBuffers, session.videos]
  );

  // Handle loading of new actions
  useInterval(() => {
    //if (prevFrameIdOnActionFetch !== currentFrameId) {
      // && currentFrameId <= lastLabeledFrameId) {
    fetchActions(currentFrameId);
    //}
  }, fetchActionsIntervalMs);

  // On initial load
  useEffect(() => {
    // Fetch Task Status
    fetchTaskStatus();
    // Fetch Game Status
    fetchGameStatus();
  }, [fetchGameStatus, fetchTaskStatus]);

  // Update next action on ball actions change
  useEffect(() => {
    if (currentFrameId >= lastLabeledFrameId) return;
    if (!nextAction && ballActions.length > 0) {
      const result: BallAction | undefined = ballActions.reverse().reduce((min, action) => {
        return action?.frame_id > currentFrameId && action?.frame_id < min?.frame_id ? action : min;
      });
      setNextAction(result);
      return;
    }
    // Trigger action happenings and store position indices
    if (nextAction && currentFrameId > nextAction?.frame_id) {
      if (!muted) {
        playBoopSound();
      }
      setActionHappenedFrameIds((prev) => [...prev, currentFrameId]);
      setNextAction(undefined);
    }
  }, [addRipple, ballActions, currentFrameId, lastLabeledFrameId, muted, nextAction, playBoopSound]);

  // Reset next action happens when it triggers
  useEffect(() => {
    setActionHappenedFrameIds((prev) => prev.filter((frameId) => frameId > currentFrameId - showActionsSeconds * fps));
  }, [currentFrameId, fps]);

  useEffect(() => {
    // if we fetched a new task status, seek to time and set last labeled frame id
    if (taskStatus?.last_frame_id) {
      setLastLabeledFrameId(taskStatus.last_frame_id);
      absoluteSeek(taskStatus.last_frame_id / fps);
    }
  }, [fps, absoluteSeek, taskStatus]);

  // Save data
  useEffect(() => {
    // Save with height indication?
    if(trySaveAction && heightIndication) {
      // Save
      saveHeightIndicationAnnotation(trySaveAction);

      // Allow new save attempts
      setHeightIndication(undefined);
      setTrySaveAction(undefined);
      
      // Play video
      setVideoPlayingState(true);
    }
  }, [heightIndication, saveHeightIndicationAnnotation, setVideoPlayingState, trySaveAction]);

  // Whenever we receive new ball positions, trigger projection for video coords
  useEffect(() => {
    // Only continue if we have atleast one buffer
    if (positionBuffers.length === 0) return;

    // Go through buffers, project
    for (let i: number = 0; i < positionBuffers.length; ++i) {
      // Check if positions have already been projected.. if so continue.
      if (positionBuffers[i].video_coordinates) continue;
      // Start projecting..
      projectPositions(i);
    }
  }, [positionBuffers, projectPositions]);

  // Load future positions
  useEffect(() => {
    if (isFetchingPositions) return;
    
    const bufferSize: number = positionBuffers.length;
    // Do we want more buffers?
    if (bufferSize < fetchPositionFutureBuffers) {
      // Fetch initial positions when buffer size is 0
      if (bufferSize === 0) {
        // Only fetch when next action is defined and we're nearing it
        if(nextAction && currentFrameId >= nextAction.frame_id) {
          fetchPositions(currentFrameId);
        }
      } else {
        const prevBuffer: PositionBuffer = positionBuffers[bufferSize - 1];
        const prevBufferIsFull: boolean = prevBuffer.page_size === fetchPositionBufferSize;
        if (prevBufferIsFull) {
          // Load next buffer
          fetchPositions(prevBuffer.page_start + fetchPositionBufferSize);
        }
      }
    }
  }, [
    currentFrameId,
    fetchPositions,
    isFetchingPositions,
    positionBuffers,
    nextAction,
  ]);

  // Handle position buffers
  useEffect(() => {
    if (positionBuffers.length === 0) return;

    const firstBuffer: PositionBuffer = positionBuffers[0];
    const lastBuffer: PositionBuffer = positionBuffers[positionBuffers.length - 1];
    if (currentFrameId > lastBuffer.page_start + lastBuffer.page_size) {
      // Remove all buffers if we're after our last buffer's end
      resetPositions();
    } else if (currentFrameId < firstBuffer.page_start) {
      // Remove all buffers if we're before our first buffer's start
      resetPositions();
    } else if (currentFrameId > firstBuffer.page_start + firstBuffer.page_size) {
      // Remove first buffer when we're surpassing our first buffer's end
      setPositionBuffers((prev: PositionBuffer[]) => {
        prev.shift();
        return [...prev];
      });
    }
  }, [currentFrameId, positionBuffers, resetPositions]);

  // Handle end of task
  useEffect(() => {
    if (currentFrameId >= endFrame) {
      if (latestAction && latestAction.type === BallActionType.END) {
        return;
      }
      setEndTaskDialog(true);
    }
  }, [currentFrameId, endFrame, latestAction]);

  const contextMenuItems: ContextItem[] = useMemo(() => {
    if (latestAction) {
      switch (latestAction.type) {
        case BallActionType.BALL_OUT:
          // Only ball in after out
          return [getContextMenuItem(BallActionType.BALL_IN)];
        case BallActionType.GAME_PAUSE:
          // Only play after pause
          return [getContextMenuItem(BallActionType.GAME_PLAY)];
        case BallActionType.KEEPER_HOLD:
        case BallActionType.KEEPER_TOUCH:
          return [
            getContextMenuItem(BallActionType.KEEPER_TOUCH), 
            getContextMenuItem(BallActionType.KEEPER_RELEASE),
            getContextMenuItem(BallActionType.GAME_PAUSE),
          ];
      }
    }
    // Return default context items
    return [
      getContextMenuItem(BallActionType.TOUCH),
      getContextMenuItem(BallActionType.BOUNCE),
      getContextMenuItem(BallActionType.ROLL),
      getContextMenuItem(BallActionType.GOAL_POST),
      getContextMenuItem(BallActionType.BALL_OUT),
      getContextMenuItem(BallActionType.GAME_PAUSE),
      getContextMenuItem(BallActionType.KEEPER_HOLD),
      getContextMenuItem(BallActionType.IDLE),
    ];
  }, [getContextMenuItem, latestAction]);

  const timeMarkerGraphics: MarkerGraphics[] = useMemo(
    () =>
      !ballActions
        ? []
        : [
            getMarkerObject(BallActionType.TOUCH),
            getMarkerObject(BallActionType.BOUNCE),
            getMarkerObject(BallActionType.ROLL),
            getMarkerObject(BallActionType.GOAL_POST),
            getMarkerObject(BallActionType.BALL_OUT),
            getMarkerObject(BallActionType.BALL_IN),
            getMarkerObject(BallActionType.GAME_PAUSE),
            getMarkerObject(BallActionType.GAME_PLAY),
            getMarkerObject(BallActionType.IDLE),
            getMarkerObject(BallActionType.END),
            getMarkerObject(BallActionType.KEEPER_HOLD),
            getMarkerObject(BallActionType.KEEPER_TOUCH),
            getMarkerObject(BallActionType.KEEPER_RELEASE),
          ],
    [ballActions, getMarkerObject]
  );

  /*
    CREATE VIDEO OVERLAY ITEMS
  */
  const overlayItems = new Map<string, VideoOverlayObject[]>();

  if (positionBuffers.length > 0 && positionBuffers[0].value && positionBuffers[0].video_coordinates) {
    // Add all 'action happened' graphics
    actionHappenedFrameIds.forEach((frameId) => {
      const index: number = frameId - positionBuffers[0].page_start;
      const currentPosition: Coordinate = positionBuffers[0].value[index];
      const ballIsInAir: boolean = (currentPosition?.z || ballGroundZ) > ballGroundZ;
      const lifetime: number = (currentFrameId - frameId) / (showActionsSeconds * fps);

      session?.videos?.forEach((video: Video) => {
        const viewOverlayObjects: VideoOverlayObject[] = overlayItems.get(String(video.id)) || [];
        const vidCoords: Coordinate[] = positionBuffers[0].video_coordinates?.get(String(video.id)) || [];
        const projectedCoord: Coordinate = vidCoords[index];
        if (!projectedCoord) return;

        const actionMarkerObjects: VideoOverlayObject[] = BallActionMarker({
          coord: projectedCoord,
          lifetime: lifetime,
          inAir: ballIsInAir,
        });

        viewOverlayObjects.push(...actionMarkerObjects);
        overlayItems.set(String(video.id), viewOverlayObjects);
      });
    });

    // Current marker graphics
    let positionIndex: number = currentFrameId - positionBuffers[0].page_start;
    const currentPosition: Coordinate = positionBuffers[0].value[positionIndex];
    const color: string = latestAction ? getActionColor(latestAction.type) : '#fff';
    const ballIsInAir: boolean = (currentPosition?.z || ballGroundZ) > ballGroundZ;
    
    session?.videos?.forEach((video: Video) => {
      const viewOverlayObjects: VideoOverlayObject[] = overlayItems.get(String(video.id)) || [];
      const vidCoords: Coordinate[] = positionBuffers[0].video_coordinates?.get(String(video.id)) || [];
      const projectedCoord: Coordinate = vidCoords[positionIndex];
      const dataAvailable: boolean = positionIndex >= 0 && positionIndex < vidCoords.length;
      const groundPositions: Map<number, Coordinate> | undefined = positionBuffers[0].ground_video_coordinates?.get(String(video.id)) || undefined;
      const groundCoord: Coordinate | undefined = groundPositions ? groundPositions.get(currentFrameId) : undefined;

      // TRAIL
      const trailCoords: Coordinate[] = [];

      if(showTrail) {
        // Then add all buffered positions
        positionBuffers.forEach((buffer: PositionBuffer) => {
          trailCoords.push(...buffer.video_coordinates?.get(String(video.id)) || []);
        });
      }

      // Create trail overlay object
      const trail: VideoOverlayObject | undefined = Trail({
        currentCoord: projectedCoord,
        trailCoords: trailCoords,
        proximityLength: proximityTrailSeconds * 0.5 * fps,
        maxX: video.width || undefined,
        maxY: video.height || undefined,
        primaryColor: '#222',
        secondaryColor: '#ddd',
      });
      // If we have a trail, add it
      trail && viewOverlayObjects.push(trail);
      // END TRAIL

      if (dataAvailable) {
        const ballMarkerObjects: VideoOverlayObject[] = BallMarker({
          coord: projectedCoord,
          centerViewOnThis: dataAvailable,
          color: color,
          inAir: ballIsInAir,
          groundCoord: groundCoord,
        });
        viewOverlayObjects.push(...ballMarkerObjects);
        overlayItems.set(String(video.id), viewOverlayObjects);
      }
    });
  }

  // Show Right click graphic
  if (lastRightClick) {
    const video: Video | undefined = session?.videos?.find((v: Video) => String(v.id) === lastRightClick.viewId);
    if (video) {
      const viewOverlayObjects: VideoOverlayObject[] = overlayItems.get(String(video.id)) || [];
      const targetCoord: Coordinate = { x: lastRightClick.info.x, y: lastRightClick.info.y };
      const groundCoord: Coordinate | undefined = heightIndication?.ground_video_coordinates.get(String(video.id));
      const clickedGraphic: VideoOverlayObject[] = BallMarker({
        coord: targetCoord,
        centerViewOnThis: false,
        color: "#0a0",
        inAir: heightIndication?.wasInAir || false,
        groundCoord: groundCoord,
      });
      viewOverlayObjects.push(...clickedGraphic);
      overlayItems.set(String(video.id), viewOverlayObjects);
    }
  }

  let mouseOverlayItem: VideoOverlayObject | undefined = undefined;
  // Keeper State Mouse Icon
  if (
    latestAction &&
    (latestAction.type === BallActionType.KEEPER_HOLD || latestAction.type === BallActionType.KEEPER_TOUCH)
  ) {
    mouseOverlayItem = {
      svg: <KeeperSvg />,
      width: 40,
      height: 40,
      center: { x: 0, y: 0 },
      centerViewOnObject: false,
    };
  }
  /* 
    END VIDEO OVERLAY ITEMS 
  */

  const debugText: string[] = ['Frame: ' + saneRound(playTime * fps)];

  const videoOverlayTexts: TextOverlay[] = [
    ...(process.env.REACT_APP_SHOW_DEBUG === 'true' ? positionBuffers[0]?.debug || [] : [])
  ];

  const viewMenuItems: JSX.Element[] = useMemo(() => {
    return [
      <ToggleMenuItem
        key="showtrail"
        value={showTrail}
        title="Trail"
        shortcut="Shift+T"
        open={lastRightClick !== undefined}
        onChange={(value: boolean) => setShowTrail(value)}
      />,
    ];
  }, [lastRightClick, showTrail]);
  
  return !taskStatus ? (
    <>Loading latest task data...</>
  ) : (
    <React.Fragment>
      {llfAsEndChoice}
      {deleteChoice}
      <AreYouSure
        open={endTaskDialog}
        title="End Task?"
        text="Are you done labeling this ball task?"
        onNo={() => setEndTaskDialog(false)}
        onYes={endTask}
      />
      <div className={classes.flexContainer} style={{ cursor: cursor }}>
        {lastRightClick && showContextMenu && (
          <LabelContextMenu
            x={lastRightClick.info.pageX}
            y={lastRightClick.info.pageY}
            items={contextMenuItems}
            onClose={onCloseContextMenu}
          />
        )}
        {lastRightClick && showSubMenu && (
          <LabelSubMenu
            x={lastRightClick.info.pageX}
            y={lastRightClick.info.pageY}
            latestAction={latestAction}
            newActionType={newActionType}
            onDone={onDoneSubMenu}
            onChange={onChangeSubMenu}
            onClose={onCloseSubMenu}
          />
        )}
        <div className={classes.sidebar}>
          <div className={classes.sidebarRow}>
            <GoBackButton path={'/session/label/' + session.id} />
            <LabelCircularProgress task={task} lastLabeledFrameId={lastLabeledFrameId} />
            <LabelDebugInfoButton debugInfo={debugText} />
            <RecalculateButton path={`${baseLabelUrl}/run/${runId}/ball/${taskId}/recalc`} />
          </div>
          <div className={classes.sidebarRow}>
            { supervisorFunctions }
            <LegacyLabelAssistantToggleButton
              checked={legacyBallActionsEnabled}
              name="legacyBallActionsToggle"
              taskType="ball actions"
              onChange={handleLegacyBallActionsToggle}
            />
          </div>
          <div className={classes.sidebarSpacer}>
            <LabelBallSidebar
              session={session}
              task={task}
              actions={ballActions}
              playTime={playTime}
              deleteAction={deleteAction}
              seekToAction={seekToAction}
              clickDelay={clickDelay}
              setClickDelay={setClickDelay}
            />
          </div>
        </div>
        <div className={classes.videoContainer}>
          <VideoLayout
            videos={session?.videos || []}
            showOverlayUI={true}
            saveId="ball-labeling"
            defaultPanMethod={PanMethod.Edge}
            updateIntervalMs={updateIntervalMs}
            videoPlaying={videoPlaying}
            onChangeVideoPlaying={setVideoPlayingState}
            playTime={playTime}
            playTimeChanged={setPlayTime}
            seekVideo={seekVideo}
            setSeekVideo={setSeekVideo}
            playbackSpeed={playbackSpeed}
            onViewMouseDown={handleVideoViewClick}
            viewOverlays={overlayItems}
            mouseOverlay={mouseOverlayItem ? [mouseOverlayItem] : []}
            textOverlays={videoOverlayTexts}
            minPlayTime={startTime}
            maxPlayTime={endTime}
            viewItems={viewMenuItems}
          />
        </div>
      </div>
      <div className={classes.flexContainer}>
        <div className={classes.videoControlsContainer}>
          <VideoControls
            isPlaying={videoPlaying}
            onChangeVideoPlaying={setVideoPlayingState}
            relativeSeek={relativeSeek}
            playbackSpeed={playbackSpeed}
            setPlaybackSpeed={setPlaybackSpeed}
            timeStep={timeStep}
            setTimeStep={setTimeStep}
          />
        </div>
        <div className={classes.timelineContainer}>
          <SessionTimeline
            playTime={playTime}
            fps={fps}
            seekToPlayTime={absoluteSeek}
            videoDuration={endTime - startTime}
            startTimeOffset={startTime}
            timeMarkers={timeMarkerGraphics}
            initialGameState={gameStatus?.start_state === GameState.ALIVE}
            gameStateTransitions={gameStatus?.transitions}
            lastLabeledTime={lastLabeledFrameId / fps}
            lastProgressionTime={lastBufferFrameId / fps}
            slidingWindow
          />
        </div>
      </div>

      {
        // Show mouse ripple effects
        rippleComponents
      }
    </React.Fragment>
  );
};

export default BallLabelTask;
